import { useEffect, useMemo, useRef, useState } from "react";

interface LoadingScreenProps {
  onComplete: () => void;
}

type Phase =
  | "loading"
  | "tension"
  | "pause"
  | "expand"
  | "done";

// Module-level singleton guard — ensures only ONE LoadingScreen ever mounts,
// even if React StrictMode or accidental double-render tries otherwise.
// This prevents duplicate bloom overlays from causing layout reflows.
let __loadingScreenMounted = false;

// ─── Dev-only mount tracking ─────────────────────────────────────────────
// Counts every mount attempt + currently-live instances so we can assert
// (in the browser console on mobile) that only one bloom overlay exists
// across route transitions. Stripped by Vite in production builds.
let __mountAttempts = 0;
let __liveInstances = 0;

// Dev-only accessors so route-change loggers can read current counts.
export function __getLoadingScreenCounts() {
  return { attempts: __mountAttempts, live: __liveInstances };
}

const dlog = (...args: unknown[]) => {
  if (import.meta.env.DEV) {
    // eslint-disable-next-line no-console
    console.log("%c[LoadingScreen]", "color:#e11; font-weight:bold;", ...args);
  }
};

export default function LoadingScreen({ onComplete }: LoadingScreenProps) {
  const instanceId = useRef(++__mountAttempts).current;
  const [allowed] = useState(() => {
    if (__loadingScreenMounted) {
      dlog(`#${instanceId} BLOCKED — singleton already held`, {
        attempts: __mountAttempts,
        live: __liveInstances,
      });
      return false;
    }
    __loadingScreenMounted = true;
    return true;
  });
  const [progress, setProgress] = useState(0);
  const [phase, setPhase] = useState<Phase>("loading");
  const [showPopup, setShowPopup] = useState(false);
  
  // Bloom overlay is a 1×1px fixed element — we scale it via `transform`
  // to cover the viewport. Keeping the layout box at 1×1 (instead of
  // 100vw×100vh) prevents Lighthouse from attributing a large layout-shift
  // score to its insertion. Transform-only animation stays CLS-safe.
  const [bloomStyle, setBloomStyle] = useState<React.CSSProperties>({
    position: "fixed",
    left: 0,
    top: 0,
    width: "1px",
    height: "1px",
    zIndex: 9998,
    pointerEvents: "none",
    transformOrigin: "0 0",
    transform: "translate(0,0) scale(0,0)",
    backgroundColor: "hsl(0 0% 5%)",
    willChange: "transform",
  });
  const barRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const mnemonicRef = useRef<HTMLAudioElement>(null);

  // Bail-out + cleanup for the duplicate-mount case
  useEffect(() => {
    if (!allowed) {
      // A previous instance is already running; immediately yield.
      onComplete();
      return;
    }
    __liveInstances++;
    dlog(`#${instanceId} MOUNTED`, {
      attempts: __mountAttempts,
      live: __liveInstances,
      route: typeof location !== "undefined" ? location.pathname : "?",
    });
    if (__liveInstances > 1) {
      // eslint-disable-next-line no-console
      console.warn(
        `[LoadingScreen] ⚠ ${__liveInstances} live instances detected — bloom overlay duplicated!`
      );
    }
    return () => {
      __liveInstances--;
      __loadingScreenMounted = false;
      dlog(`#${instanceId} UNMOUNTED`, { live: __liveInstances });
    };
  }, [allowed, onComplete, instanceId]);

  // Dev-only: log when the bloom overlay enters/leaves the DOM
  useEffect(() => {
    if (!import.meta.env.DEV || !allowed) return;
    const visible = phase === "expand" || phase === "pause";
    if (visible) dlog(`#${instanceId} bloom overlay rendered (phase=${phase})`);
  }, [phase, allowed, instanceId]);

  // Play mnemonic on mount; fall back to first interaction if autoplay is blocked.
  // Successful mnemonic playback also unlocks the audio context, allowing the
  // main playback engine to autoplay once the loading screen finishes.
  useEffect(() => {
    if (!allowed) return;
    const audio = mnemonicRef.current;
    if (!audio) return;
    audio.currentTime = 0;
    audio.volume = 0.6;
    const playPromise = audio.play();
    if (playPromise !== undefined) {
      playPromise.catch(() => {
        const resume = () => { audio.play().catch(() => {}); };
        window.addEventListener("pointerdown", resume, { once: true, capture: true, passive: true });
        window.addEventListener("touchstart", resume, { once: true, capture: true, passive: true });
        window.addEventListener("keydown", resume, { once: true, capture: true });
      });
    }
  }, [allowed]);

  // Eased progress: normal until 90, then cubic ease-in-out for tension
  const easedProgress = useMemo(() => {
    if (progress < 90) return progress;
    const t = (progress - 90) / 10;
    return 90 + (1 - Math.pow(1 - t, 3)) * 10;
  }, [progress]);

  // Drive progress from 0→100
  useEffect(() => {
    const startTime = performance.now();
    const duration = 1400;

    const tick = (now: number) => {
      const elapsed = now - startTime;
      const p = Math.min(elapsed / duration, 1);
      const raw = p * 100;
      setProgress(raw);

      if (p < 1) {
        requestAnimationFrame(tick);
      }
    };

    requestAnimationFrame(tick);
  }, []);

  // Phase transitions driven by progress
  useEffect(() => {
    if (progress >= 90 && progress < 100 && phase === "loading") {
      setPhase("tension");
    }
    if (progress >= 100 && (phase === "tension" || phase === "loading")) {
      setPhase("pause");
    }
  }, [progress, phase]);

  // pause → expand: snapshot the bar's rect, then start at scale matching the bar
  useEffect(() => {
    if (phase !== "pause") return;
    const t = setTimeout(() => {
      const bar = barRef.current;
      if (bar) {
        const rect = bar.getBoundingClientRect();
        // Element is 1×1px; scale equals target px size.
        const sx = Math.max(rect.width, 0.001);
        const sy = Math.max(rect.height, 0.001);
        setBloomStyle((prev) => ({
          ...prev,
          transform: `translate(${rect.left}px, ${rect.top}px) scale(${sx}, ${sy})`,
          transition: "none",
        }));
      }
      setShowPopup(true);
      setPhase("expand");
    }, 500);
    return () => clearTimeout(t);
  }, [phase]);

  // expand → bloom to full screen via transform (no layout shift)
  useEffect(() => {
    if (phase !== "expand") return;
    const raf = requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        const vw = window.innerWidth || 1;
        const vh = window.innerHeight || 1;
        setBloomStyle((prev) => ({
          ...prev,
          // Element is 1×1px; scale to viewport size.
          transform: `translate(0px, 0px) scale(${vw}, ${vh})`,
          transition:
            "transform 700ms cubic-bezier(0.83,0,0.17,1)",
        }));

        setTimeout(() => {
          setPhase("done");
          onComplete();
        }, 700);
      });
    });
    return () => cancelAnimationFrame(raf);
  }, [phase, onComplete]);

  // If a duplicate instance, render nothing — the singleton effect already called onComplete.
  if (!allowed) return null;

  return (
    <>
      {/* ── Mnemonic audio ── */}
      <audio ref={mnemonicRef} src="/mnemonic.wav" preload="auto" data-role="mnemonic" />
      {/* ── Loading screen ── */}
      <div
        ref={containerRef}
        className={`fixed inset-0 z-[200] bg-background flex flex-col items-center justify-center transition-opacity duration-400 ${
          showPopup ? "opacity-0 pointer-events-none" : "opacity-100"
        }`}
        style={{ transitionDuration: "300ms" }}
      >
        {/* Wordmark */}
        <div className="mb-10 relative select-none">
          <span
            className="font-serif text-2xl tracking-tight"
            style={{ color: "hsl(0 0% 60% / 0.2)" }}
          >
            LoveLanguage<span>.fm</span>
          </span>
          {/* Filled overlay — clips left-to-right */}
          <span
            className="font-serif text-2xl tracking-tight absolute inset-0 overflow-hidden whitespace-nowrap"
            style={{
              width: `${easedProgress}%`,
              transition: "width 0.05s linear",
              color: "hsl(0 0% 60%)",
            }}
          >
            LoveLanguage<span style={{ color: "#FFFFFF" }}>.fm</span>
          </span>
        </div>

        {/* Progress bar */}
        <div ref={barRef} className="w-32 h-px bg-border overflow-hidden">
          <div
            className="h-full bg-absa-red origin-left"
            style={{
              width: `${easedProgress}%`,
              transition: "width 0.05s linear",
            }}
          />
        </div>
      </div>

      {/* ── Bloom overlay ── single instance, removed once flow completes */}
      {showPopup && phase !== "done" && (
        <div style={bloomStyle} aria-hidden="true" />
      )}
    </>
  );
}
